import axios from 'axios';
import { getTokenData, saveTokenData, setAuthMode, getAuthMode, AUTH_MODE, clearTokenData } from '../utils/token';

export default (() => {
  const clientId = process.env.VUE_APP_CONDUCTOR_CLIENT_ID || '';
  const basePath = process.env.VUE_APP_AUTH_SERVER || '';
  const accessServerURL = process.env.VUE_APP_ACCESS_SERVER || '';
  const tokenPath = '/oauth/token';
  const tokenRevokePath = '/oauth2/revoke';
  const accessPath = '/access';
  const accessV2Path = '/access/v2';

  let refreshTokenInProgress;
  let refreshTokenPromise;

  function saveToken(data) {
    const tokenData = Object.assign({ last_refresh: Date.now() }, data);
    
    // Convert expires_in to milliseconds if it's in seconds
    if (tokenData.expires_in && tokenData.expires_in < 1000000) {
      tokenData.expires_in = tokenData.expires_in * 1000;
    }
    
    saveTokenData(tokenData);
    return tokenData;
  }

  function saveJwt(token){
    const tokenData = {
      access_token: token,
      token_type: "bearer",
    }
    saveTokenData(tokenData);
    return tokenData;
  }

  /**
   * Login with httpOnly cookies (PRIMARY METHOD)
   * Uses /sso/proxy-login2 endpoint that sets httpOnly cookies
   * Falls back to localStorage mode if cookies fail
   */
  async function login(username, password) {
    const credentials = `${username}:${password}`;
    const encodedCredentials = btoa(credentials);
    
    // TRY COOKIES MODE FIRST (secure)
    try {
      const cookieUrl = accessServerURL
        .concat(accessPath)
        .concat('/sso/proxy-login2');
      
      const response = await axios({
        method: 'post',
        url: cookieUrl,
        data: {  
          clientId: clientId,
          encodedCredentials: encodedCredentials
        },
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true // CRITICAL: Send/receive cookies
      });
      
      // Success with cookies!
      setAuthMode(AUTH_MODE.COOKIES);
      return saveToken(response.data);
      
    } catch (cookieError) {
      
      // FALLBACK TO LOCALSTORAGE MODE
      try {
        const localStorageUrl = accessServerURL
          .concat(accessPath)
          .concat('/sso/proxy-login');
        
        const response = await axios({
          method: 'post',
          url: localStorageUrl,
          data: {  
            clientId: clientId,
            encodedCredentials: encodedCredentials
          },
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        setAuthMode(AUTH_MODE.LOCALSTORAGE);
        return saveToken(response.data);
        
      } catch (localStorageError) {
        throw localStorageError;
      }
    }
  }

  async function SSO(username, host) {
    const url = accessServerURL
      .concat(accessPath)
      .concat('/sso/login');
    const response = await axios({
      method: 'get',
      url,
      params: {
        email: username,
        relayState: host,
      },
    });
    return response.data;
  }

  function checkLoggedIn() {
    const tokenData = getTokenData();
    if (tokenData && tokenData.access_token) {
      return tokenData;
    }
    return null;
  }

  async function logout() {
    const mode = getAuthMode();
    
    // Call appropriate logout endpoint
    if (mode === AUTH_MODE.COOKIES) {
      try {
        const url = accessServerURL
          .concat(accessPath)
          .concat('/sso/proxy-logout2');
        
        await axios({
          method: 'post',
          url,
          withCredentials: true // Send cookies to be cleared
        });
      } catch (error) {
        console.warn('⚠️ Cookie logout failed:', error.message);
      }
    }
    
    // Always revoke token (works for both modes)
    await revokeToken();
    
    // Clear all local data
    clearTokenData();
    localStorage.removeItem('my-app');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('orgId');
    localStorage.removeItem('chmln:l.C.d');
    localStorage.removeItem('siteId');
    
    console.log('🗑️ Logout complete, reloading page...');
    //reload needed to reset Google Analytics, if not reseted and logged in with a different account he old user-id will persist
    window.location.reload();
  }

  async function forgot(username) {
    const url = accessServerURL
      .concat(accessV2Path)
      .concat('/users/forgotPassword');
    const response = await axios({
      method: 'get',
      url,
      params: {
        email: username,
        clientId: clientId,
      },
    });
    return response.data;
  }

  async function reset(username, password, key) {
    const url = accessServerURL.concat(accessPath).concat('/users/verify');
    const response = await axios({
      method: 'post',
      url,
      data: {
        email: username,
        newPassword: password,
        verificationKey: key,
      },
    });
    return response.data;
  }

  async function revokeToken() {
    const url = basePath.concat(tokenRevokePath);
    const accessToken = await getAccessToken();
    try {
      await axios({
        method: 'post',
        url,
        headers: {
          'Content-Type': 'text/plain',
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: 'text',
      });
    } catch (error) {
      error;
    }
  }

  /**
   * Refreshes the Access and Refresh Tokens
   *
   * This function only uses only concurrent api call, it reuses a pending api call if it is in progress
   *
   * @returns {Promise} Promise of Token data object
   */
  async function refreshToken() {
    if (!refreshTokenInProgress) {
      refreshTokenInProgress = true;
      refreshTokenPromise = new Promise((resolve, reject) => {
        refreshTokenCall()
          .then(function (response) {
            resolve(response);
          })
          .catch(function (error) {
            reject(error);
          })
          .finally(function () {
            refreshTokenInProgress = false;
          });
      });
    }
    return refreshTokenPromise;
  }

  async function refreshTokenCall() {
    const mode = getAuthMode();
    
    // TRY COOKIES MODE FIRST
    if (mode === AUTH_MODE.COOKIES) {
      try {
        const cookieUrl = accessServerURL
          .concat(accessPath)
          .concat('/sso/proxy-refresh2');
        
        const response = await axios({
          method: 'post',
          url: cookieUrl,
          data: {
            clientId: clientId
          },
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true // Send refresh_token cookie
        });
        
        return saveToken(response.data);
        
      } catch (cookieError) {
        // Don't fallback on refresh - if cookies fail, user needs to re-login
        throw cookieError;
      }
    }
    
    // LOCALSTORAGE MODE
    const refreshToken = getRefreshTokenSync();   
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const url = accessServerURL
      .concat(accessPath)
      .concat('/sso/proxy-refresh');
    
    try {
      const response = await axios({
        method: 'post',
        url,
        data: {
          clientId: clientId,
          refresh_token: refreshToken
        },
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      // Validate response has required fields
      if (!response.data.access_token) {
        throw new Error('Invalid refresh response: missing access_token');
      }
      
      return saveToken(response.data);
      
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check if any Refresh Token call is in progress and wait for it
   *
   * @returns {Promise<undefined>} Promise which will resolve when no Refresh Token call is in progress
   */
  async function checkRefreshCallNotPending() {
    if (refreshTokenInProgress) {
      try {
        await refreshToken();
      } catch (e) {
        e;
      }
    }
  }

  /**
   * Gets the stored Access Token immediately but does not checks for an ongoing Refresh Token call
   *
   * The Access Token returned from this function may or may not be invalidated due to
   * any ongoing Refresh Token call and can also be expired
   *
   * Usage of this function is not recommended, you should instead use the Promise version
   * @see getAccessToken for the Promise version of Get Access Token
   *
   * @returns {string|null} Access Token which can be null if not present in application
   */
  function getAccessTokenSync() {
    const tokenData = getTokenData();
    const mode = getAuthMode();
    
    // In cookies mode, tokens are in httpOnly cookies (not accessible to JS)
    // Return null so axios interceptor doesn't add Authorization header
    // HybridAuthFilter in backend will read cookies and add the header
    if (mode === AUTH_MODE.COOKIES) {
      return null;
    }
    
    // In localStorage mode, return the token
    return (tokenData && tokenData.access_token) || null;
  }

  /**
   * Gets the stored Access Token but checks for an ongoing Refresh Token call first
   *
   * The Access Token returned from this function will not be invalidated due to
   * any ongoing Refresh Token call but can be expired
   *
   * @returns {Promise<string|null>} Promise of an Access Token, which can be null if not present in application
   */
  async function getAccessToken() {
    await checkRefreshCallNotPending();
    return getAccessTokenSync();
  }

  function getRefreshTokenSync() {
    const tokenData = getTokenData();
    const mode = getAuthMode();
    
    // In cookies mode, refresh_token is in httpOnly cookie
    if (mode === AUTH_MODE.COOKIES) {
      return null;
    }
    
    // In localStorage mode, return the refresh token
    return (tokenData && tokenData.refresh_token) || null;
  }

  async function getTableauToken() {
    const payload = {
      userName: 'temp'
    };

    const url = "https://c2pesp01-usea1b.link-labs.com/auth";
    const response = await axios({
      method: 'post',
      url,
      data: payload,
      withCredentials: false // Disable credentials for Tableau server (CORS wildcard)
    });
    return response.data;
  }

  return {
    login,
    SSO,
    saveJwt,
    checkLoggedIn,
    logout,
    forgot,
    reset,
    refreshToken,
    getAccessTokenSync,
    getAccessToken,
    getRefreshTokenSync,
    getTableauToken,
  };
})();
